import * as Sentry from "@sentry/react";
import {
  hasRecentChunkReload,
  isChunkLoadError,
  reloadForStaleChunks,
  reloadWithCacheBust,
  shouldDropSentryEvent,
} from "./_stream/helpers/chunkError";

// Only initialize Sentry in production
if (
  import.meta.env.VITE_SENTRY_DSN &&
  import.meta.env.VITE_APP_ENV === "production"
) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    beforeSend(event, hint) {
      if (shouldDropSentryEvent(event, hint)) {
        return null;
      }
      return event;
    },
  });
}
import { createRoot } from "react-dom/client";
import { lazy, Suspense } from "react";
import axios from "axios";
import { StreamI18nProvider, getDirection } from "./_stream/i18n/Streami18n";
import {
  LayoutSplashScreen,
  SplashScreenProvider,
} from "./_stream/layout/core";

import { AuthProvider } from "./app/modules/auth/core/Auth";
import { setupAxios } from "./app/modules/auth/core/AuthHelpers";
import { RootContext, RootStore } from "./store";
import { initAnalytics } from "./_stream/helpers/analytics";
import { QueryClient, QueryClientProvider } from "react-query";
import { ThemeProvider } from "@mui/material/styles";
import { menuMotionTheme } from "./_stream/MUI/menuMotionTheme";

const AppRoutes = lazy(() =>
  import("./routes/AppRoutes").then((module) => ({
    default: module.AppRoutes,
  }))
);

const recaptchaKey = import.meta.env.VITE_GOOGLE_RECAPTCHA_KEY;
const recaptchaScript = document.createElement("script");
recaptchaScript.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaKey}`;
recaptchaScript.async = true;
document.head.appendChild(recaptchaScript);

setupAxios(axios);

initAnalytics({ orgId: import.meta.env.VITE_FULLSTORY_ORG_ID });

const initialDirection = getDirection();
document.documentElement.setAttribute("dir", initialDirection);
document.body.setAttribute("dir", initialDirection);
document.body.style.direction = initialDirection;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dashboards don't need second-fresh data. Treat data as fresh for 60s so revisiting a route (or toggling back to a cac...
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});
const container = document.getElementById("root");

window.addEventListener("vite:preloadError", (event) => {
  if (reloadForStaleChunks()) {
    event.preventDefault();
  }
});

window.addEventListener("unhandledrejection", (event) => {
  const msg = event.reason?.message ?? "";
  if (isChunkLoadError(msg) && reloadForStaleChunks()) {
    event.preventDefault();
  }
});

if (container) {
  const root = createRoot(container);

  root.render(
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => {
        const msg = error instanceof Error ? error.message : String(error);
        if (isChunkLoadError(msg)) {
          const alreadyReloaded = hasRecentChunkReload();
          return (
            <div style={{ padding: 20, textAlign: "center" }}>
              <p>
                {alreadyReloaded
                  ? "Failed to load. The app may be temporarily unavailable."
                  : "Loading latest version..."}
              </p>
              <button onClick={reloadWithCacheBust}>Refresh</button>
            </div>
          );
        }
        return (
          <div role="alert" style={{ padding: 20, textAlign: "center" }}>
            <p>Something went wrong:</p>
            <pre style={{ whiteSpace: "normal" }}>{msg}</pre>
            <button onClick={resetError}>Retry</button>
          </div>
        );
      }}
    >
      <RootContext.Provider value={RootStore}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={menuMotionTheme}>
            <StreamI18nProvider>
              <AuthProvider>
                <SplashScreenProvider>
                  <Suspense fallback={<LayoutSplashScreen />}>
                    <AppRoutes />
                  </Suspense>
                </SplashScreenProvider>
              </AuthProvider>
            </StreamI18nProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </RootContext.Provider>
    </Sentry.ErrorBoundary>
  );
}
